
import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Button, Checkbox, FormControlLabel, CircularProgress, Alert, Collapse } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { Save, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useApplication } from '../contexts/ApplicationContext';
import { useMenu } from '../contexts/MenuContext';
import { getMenuForApp, MenuItem as MenuItemType, resolveIcon } from '../config/menuConfig';
import blobticService from '../services/blobticService';
import { blobticMessage } from '../components/BlobticMessage';
import GenericPage from '../components/GenericPage';
import { ZoomField } from '../components/ZoomField';
import { ZoomData } from '../services/blobticZoomService';
import { useLanguage } from '../contexts/LanguageContext';

export const MenuConfigPage: React.FC = () => {
    const { isAuthenticated, user } = useAuth();
    const { currentApp } = useApplication();
    
    const { t } = useLanguage();



    // State
    const [selectedProfile, setSelectedProfile] = useState<any>(null); // Store full profile object
    const [currentMenuConfig, setCurrentMenuConfig] = useState<MenuItemType[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [footerInfo, setFooterInfo] = useState<{ lastUpdate?: string; userLib?: string }>({});

    // Load Menu for selected profile and app
    useEffect(() => {
        if (!selectedProfile || !currentApp) {
            setCurrentMenuConfig([]);
            setFooterInfo({});
            return;
        }

        const loadProfileMenu = async () => {
            setLoading(true);
            try {
                const sanitizeMenu = (items: MenuItemType[]): MenuItemType[] => {
                    return items.map(item => {
                        const newItem = { ...item };
                        // Sanitize corrupted icon objects
                        if (typeof newItem.icon === 'object' && newItem.icon !== null) {
                            newItem.icon = undefined;
                        }
                        if (newItem.child && newItem.child.length > 0) {
                            newItem.child = sanitizeMenu(newItem.child);
                        }
                        return newItem;
                    });
                };

                // 1. Get default menu structure
                let defaultStructure: MenuItemType[] = [];
                try {
                    const defaultResponse = await blobticService.executeZoomRequest<any>({
                        ZoomKey: 'config_menu',
                        Action: {
                            OKey: 'Blobtic_Sys_Profil,config_menu,get_default_menu',
                            APP_NAME: currentApp
                        }
                    });

                    if (defaultResponse.success && defaultResponse.data) {
                        let data = defaultResponse.data;
                        // Handle array response (backend might wrap data in list)
                        if (Array.isArray(data) && data.length > 0) {
                            const first = data[0];
                            // Check if it's the wrapper object or just a list of items
                            if (first && (typeof first === 'object') && ('menuJson' in first || 'Menu' in first)) {
                                data = first;
                            }
                        }

                        if (data.menuJson) {
                            defaultStructure = JSON.parse(data.menuJson);
                        } else if (data.Menu) {
                            // Handle case if returning { Menu: ... } like other actions
                            defaultStructure = JSON.parse(data.Menu);
                        } else if (Array.isArray(data)) {
                            // Direct array of items
                            defaultStructure = data;
                        }

                        // Sanitize defaults
                        defaultStructure = sanitizeMenu(defaultStructure);
                    }
                } catch (e) {
                    console.error("Failed to load default menu structure", e);
                }

                // 2. Get saved config from DB
                const response = await blobticService.executeZoomRequest<any>({
                    ZoomKey: 'config_menu',
                    Action: {
                        OKey: 'Blobtic_Sys_Profil,config_menu,get_profil_menu',
                        APP_NAME: currentApp,
                        PRF_IDF: selectedProfile.PRF_IDF
                    }
                });

                let savedMenu: MenuItemType[] | null = null;

                if (response.success && response.data) {
                    let data = response.data;
                    // Handle array response (backend might wrap data in list)
                    if (Array.isArray(data) && data.length > 0) {
                        const first = data[0];
                        if (first && (typeof first === 'object') && ('menuJson' in first || 'Menu' in first)) {
                            data = first;
                        }
                    }

                    console.log("[MenuConfig] Processed data:", data);

                    // Handle new response format with metadata
                    if (data.menuJson) {
                        try {
                            savedMenu = JSON.parse(data.menuJson);
                        } catch (e) {
                            console.error("[MenuConfig] Error parsing saved menu json", e);
                        }
                    } else if (typeof data === 'string') {
                        // Fallback
                        try {
                            savedMenu = JSON.parse(data);
                        } catch (e) { }
                    }

                    if (savedMenu) {
                        savedMenu = sanitizeMenu(savedMenu);
                    }

                    setFooterInfo({
                        lastUpdate: data.lastUpdate ? new Date(data.lastUpdate).toLocaleString() : undefined,
                        userLib: data.userLib
                    });
                } else {
                    setFooterInfo({});
                }

                // 3. Merge Logic
                const mapMenuState = (defaults: MenuItemType[], saved: MenuItemType[] | null): MenuItemType[] => {
                    return defaults.map(defItem => {
                        let isIncluded = true;
                        if (saved) {
                            const savedItem = saved.find(s =>
                                (s.translationKey && s.translationKey === defItem.translationKey) ||
                                (s.caption && s.caption === defItem.caption)
                            );

                            if (!savedItem) {
                                isIncluded = false;
                            }
                        }

                        const newItem = { ...defItem, _checked: isIncluded };

                        if (defItem.child && defItem.child.length > 0) {
                            const savedParent = saved?.find(s =>
                                (s.translationKey && s.translationKey === defItem.translationKey) ||
                                (s.caption && s.caption === defItem.caption)
                            );

                            newItem.child = mapMenuState(defItem.child, savedParent ? savedParent.child || [] : (saved ? [] : null));
                        }

                        return newItem;
                    });
                };

                const merged = mapMenuState(defaultStructure, savedMenu);
                setCurrentMenuConfig(merged);

            } catch (err) {
                console.error("Error loading menu config", err);
                blobticMessage.error(t('common.messages.error'), t('page.menuConfig.error.load'));
            } finally {
                setLoading(false);
            }
        };

        loadProfileMenu();
    }, [selectedProfile, currentApp, blobticMessage.error, t]);


    // Toggle Handler
    const handleToggle = (item: any, parent: any | null, checked: boolean) => {
        const newConfig = JSON.parse(JSON.stringify(currentMenuConfig));

        const updateItem = (items: any[]) => {
            for (const i of items) {
                if ((i.translationKey && i.translationKey === item.translationKey) || (i.caption === item.caption)) {
                    i._checked = checked;
                    if (!checked && i.child) {
                        i.child.forEach((c: any) => c._checked = false);
                    }
                    return true;
                }
                if (i.child) {
                    if (updateItem(i.child)) return true;
                }
            }
            return false;
        };

        updateItem(newConfig);
        setCurrentMenuConfig(newConfig);
    };


    const handleSave = async () => {
        if (!selectedProfile || !currentApp) return;
        setSaving(true);

        try {
            const filterChecked = (items: any[]): any[] => {
                return items
                    .filter(i => i._checked)
                    .map(i => {
                        const { _checked, ...rest } = i;
                        const newItem = { ...rest };
                        if (newItem.child && newItem.child.length > 0) {
                            newItem.child = filterChecked(newItem.child);
                        }
                        return newItem;
                    });
            };

            const menuToSave = filterChecked(currentMenuConfig);

            blobticMessage.success(t('common.messages.success'), t('page.menuConfig.success.save'));

            // Reload metadata (Last update / User) from response
            const responseData = (await blobticService.executeZoomRequest<any>({
                ZoomKey: 'config_menu',
                Action: {
                    OKey: 'Blobtic_Sys_Profil,config_menu,save_menu',
                    PRF_IDF: selectedProfile.PRF_IDF,
                    APP_NAME: currentApp,
                    PRFM_MENU_JSON: JSON.stringify(menuToSave)
                }
            })).data;

            if (responseData) {
                setFooterInfo({
                    lastUpdate: responseData.lastUpdate ? new Date(responseData.lastUpdate).toLocaleString() : new Date().toLocaleString(),
                    userLib: responseData.userLib || user?.name
                });
            } else {
                // Fallback if no specific data returned (should not happen with new backend)
                setFooterInfo(prev => ({
                    ...prev,
                    lastUpdate: new Date().toLocaleString(),
                    userLib: user?.name
                }));
            }

        } catch (err) {
            console.error("Error saving menu", err);
            blobticMessage.error(t('common.messages.error'), t('page.menuConfig.error.save'));
        } finally {
            setSaving(false);
        }
    };

    const handleProfileChange = (val: string, data?: ZoomData) => {
        if (data) setSelectedProfile(data);
        else if (!val) setSelectedProfile(null);
    };

    return (
        <>
            <GenericPage
                title={t('page.menuConfig.title')}
                icon={<Save />} // Using Save icon as defined in previous page, or could be Settings/Tune
                footerInfo={footerInfo}
                additionalActions={
                    <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                        onClick={handleSave}
                        disabled={saving || !selectedProfile}
                    >
                        {t('common.actions.save')}
                    </Button>
                }
            >
                <Box sx={{ mb: 3, maxWidth: 400 }}>
                    {/* Replace Profile Select with ZoomField */}
                    <ZoomField
                        zoomKey="profil"
                        label={t('page.menuConfig.profil.label')}
                        placeholder={t('page.menuConfig.profil.placeholder')}
                        value={selectedProfile ? selectedProfile.PRF_CODE : ''}
                        displayField="PRF_CODE" // Or PRF_LIB
                        valueField="PRF_IDF"
                        onChange={handleProfileChange}
                        showSelectedInfo={true}
                        helperText={selectedProfile ? selectedProfile.PRF_LIB : t('page.menuConfig.profil.helper')}
                        required
                    />
                </Box>

                <Box sx={{ borderTop: '1px solid #eee', pt: 2 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                                {selectedProfile ? t('page.menuConfig.structure.title').replace('{0}', currentApp || '') : t('page.menuConfig.structure.empty')}
                            </Typography>

                            {currentMenuConfig.map((item, idx) => (
                                <ConfigTreeItem
                                    key={idx}
                                    item={item}
                                    handleToggle={handleToggle}
                                    t={t}
                                    level={0}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </GenericPage>


        </>
    );
};

// Sub-component for rendering tree items with collapse logic
interface ConfigTreeItemProps {
    item: any;
    handleToggle: (item: any, parent: any | null, checked: boolean) => void;
    t: (key: string) => string;
    level: number;
}

const ConfigTreeItem: React.FC<ConfigTreeItemProps> = ({ item, handleToggle, t, level }) => {
    // Open by default if it's top level (level 0), closed otherwise? Or always open?
    // User requested collapsible, usually implies starting open or having ability to close.
    // Let's start open for better visibility of what is checked.
    const [open, setOpen] = useState(true);
    const hasChildren = item.child && item.child.length > 0;
    const iconNode = resolveIcon(item);

    const handleExpandClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setOpen(!open);
    };

    return (
        <Box sx={{ ml: level * 3, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {hasChildren && (
                    <IconButton size="small" onClick={handleExpandClick} sx={{ mr: 0.5, p: 0.5 }}>
                        {open ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                )}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!item._checked}
                            onChange={(e) => handleToggle(item, null, e.target.checked)}
                            sx={{ py: 0.5 }} // reduce padding for compactness
                        />
                    }
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {iconNode && (
                                <Box sx={{ display: 'flex', color: 'text.secondary', '& svg': { fontSize: 20 } }}>
                                    {iconNode}
                                </Box>
                            )}
                            <Typography>
                                {item.translationKey ? t(item.translationKey) : item.caption}
                            </Typography>
                        </Box>
                    }
                    sx={{ ml: hasChildren ? 0 : 3.5 }} // Indent leaf nodes to align with text of parents (icon button width approx)
                />
            </Box>

            {hasChildren && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <Box>
                        {item.child.map((child: any, idx: number) => (
                            <ConfigTreeItem
                                key={idx}
                                item={child}
                                handleToggle={handleToggle}
                                t={t}
                                level={level + 1}
                            />
                        ))}
                    </Box>
                </Collapse>
            )}
        </Box>
    );
};
